/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

/* Import configuration */
import type {
    OverviewAnomalyConfig,
    OverviewAnomalyKpiConfig,
} from '@/config/kpi/overviewAnomalyConfig';

/* Import types */
import type { Api2ValueDataItem } from '@/lib/overview/api2ValueData';
import {
    getOverviewStatusCategory,
    type OverviewCardSortData,
    type OverviewSortDirection,
    type OverviewStatusCounts,
} from '@/lib/overview/overviewCards';

export const SYSTEM_SENSOR_COUNT_SORT_KEY = 'sensorCount';
export const STATUS_SENSOR_COUNT_SORT_KEYS = [
    'dangerSensorCount',
    'warningSensorCount',
    'successSensorCount',
] as const;
type StatusSensorCountSortKey = typeof STATUS_SENSOR_COUNT_SORT_KEYS[number];

const STATUS_SENSOR_COUNT_SORT_KEY_TO_STATUS: Record<
    StatusSensorCountSortKey,
    keyof OverviewStatusCounts
> = {
    dangerSensorCount: 'danger',
    successSensorCount: 'success',
    warningSensorCount: 'warning',
};

export interface OverviewSystemAggregationSource extends OverviewCardSortData {
    address?: string | null;
    anomalyConfig: OverviewAnomalyConfig;
    errorMessage: string;
    item: Api2ValueDataItem;
    name?: string;
    status: number;
}

export interface OverviewSystemKpiData extends OverviewAnomalyKpiConfig {
    anomalyConfig: OverviewAnomalyConfig;
    value: number | null;
}

/** Keeps each sensor calculation available for the system overview information modal. */
export interface OverviewSystemStatusCalculation {
    anomalyConfig: OverviewAnomalyConfig;
    item: Api2ValueDataItem;
    sensor: string;
    status: number;
}

export interface OverviewAnomalySensorCount {
    anomaly: string;
    sensorCount: number;
}

export interface OverviewAnomalyIssueCount {
    anomaly: string;
    sensorCount: number;
}

export interface OverviewAggregatedCardData extends OverviewCardSortData {
    anomalies: string[];
    anomalyIssueCounts: OverviewAnomalyIssueCount[];
    anomalySensorCounts: OverviewAnomalySensorCount[];
    customers: string[];
    errorMessages: string[];
    kpis: OverviewSystemKpiData[];
    sensorCount: number;
    status: number;
    statusCalculations: OverviewSystemStatusCalculation[];
    statusCounts: OverviewStatusCounts;
    systems: string[];
}

export interface OverviewSystemCardData extends OverviewAggregatedCardData {
    address: string | null;
    name: string;
}

export interface OverviewAnomalyCardData extends OverviewAggregatedCardData {
    anomalyConfig: OverviewAnomalyConfig;
}

export interface OverviewStatusCardData extends OverviewAggregatedCardData {
    statusCategory: ReturnType<typeof getOverviewStatusCategory>;
}

export interface OverviewCustomerCardData extends OverviewAggregatedCardData {
}

interface PendingKpiAggregation {
    anomalyConfig: OverviewAnomalyConfig;
    config: OverviewAnomalyKpiConfig;
    values: number[];
}

function aggregateValues(
    values: number[],
    aggregation: OverviewAnomalyKpiConfig['systemAggregation'],
): number | null {
    if (values.length === 0) {
        return null;
    }

    switch (aggregation) {
        case 'average':
            return values.reduce((sum, value) => sum + value, 0) / values.length;
        case 'max':
            return Math.max(...values);
        case 'sum':
            return values.reduce((sum, value) => sum + value, 0);
        default:
            return null;
    }
}

function getSystemGroupKey(card: OverviewSystemAggregationSource): string {
    return card.system || `device:${card.sensor}`;
}

function getStatusCounts(
    cards: OverviewSystemAggregationSource[],
): OverviewStatusCounts {
    return cards.reduce<OverviewStatusCounts>((counts, card) => {
        counts[getOverviewStatusCategory(card.status)] += 1;

        return counts;
    }, {
        danger: 0,
        success: 0,
        warning: 0,
    });
}

function getAnomalyIssueCounts(
    cards: OverviewSystemAggregationSource[],
): OverviewAnomalyIssueCount[] {
    return Array.from(cards.reduce<Map<string, number>>((counts, card) => {
        if (card.anomaly && getOverviewStatusCategory(card.status) !== 'success') {
            counts.set(card.anomaly, (counts.get(card.anomaly) ?? 0) + 1);
        }

        return counts;
    }, new Map()).entries()).map(([anomaly, sensorCount]) => ({ anomaly, sensorCount }));
}

function aggregateKpis(
    cards: OverviewSystemAggregationSource[],
): OverviewSystemKpiData[] {
    const pendingKpis = new Map<string, PendingKpiAggregation>();

    cards.forEach((card) => {
        card.anomalyConfig.kpis.forEach((kpi) => {
            const aggregationKey = `${card.anomalyConfig.group}:${kpi.sensorType}`;
            const pendingKpi = pendingKpis.get(aggregationKey) ?? {
                anomalyConfig: card.anomalyConfig,
                config: kpi,
                values: [],
            };
            const rawValue = card.item.rawValue.get(kpi.sensorType);

            if (rawValue !== null && Number.isFinite(rawValue)) {
                pendingKpi.values.push(rawValue);
            }

            pendingKpis.set(aggregationKey, pendingKpi);
        });
    });

    return Array.from(pendingKpis.values()).map(({ anomalyConfig, config, values }) => {
        const aggregatedValue = aggregateValues(values, config.systemAggregation);

        return {
            ...config,
            anomalyConfig,
            value: aggregatedValue === null
                ? null
                : aggregatedValue * config.conversion,
        };
    });
}

function createAggregatedCardData(
    cards: OverviewSystemAggregationSource[],
    sensor: string,
): OverviewAggregatedCardData {
    const anomalySensorCounts = Array.from(cards.reduce<Map<string, number>>((counts, card) => {
        if (card.anomaly) {
            counts.set(card.anomaly, (counts.get(card.anomaly) ?? 0) + 1);
        }

        return counts;
    }, new Map()).entries()).map(([anomaly, sensorCount]) => ({ anomaly, sensorCount }));
    const anomalyIssueCounts = getAnomalyIssueCounts(cards);
    const anomalies = anomalySensorCounts.map(({ anomaly }) => anomaly);
    const customers = Array.from(new Set(
        cards.map((card) => card.customer).filter(Boolean),
    ));
    const systems = Array.from(new Set(
        cards.map((card) => card.system).filter(Boolean),
    ));

    return {
        anomalies,
        anomaly: anomalies.join(', '),
        anomalyIssueCounts,
        anomalySensorCounts,
        customer: customers.join(', '),
        customers,
        errorMessages: Array.from(new Set(
            cards.map((card) => card.errorMessage).filter(Boolean),
        )),
        kpis: aggregateKpis(cards),
        sensor,
        sensorCount: cards.length,
        status: Math.max(...cards.map((card) => card.status), 0),
        statusCalculations: cards.map(({ anomalyConfig, item, sensor: deviceName, status }) => ({
            anomalyConfig,
            item,
            sensor: deviceName,
            status,
        })),
        statusCounts: getStatusCounts(cards),
        system: systems.join(', '),
        systems,
    };
}

export function aggregateOverviewCardsBySystem(
    cards: OverviewSystemAggregationSource[],
): OverviewSystemCardData[] {
    const groupedCards = new Map<string, OverviewSystemAggregationSource[]>();

    cards.forEach((card) => {
        const groupKey = getSystemGroupKey(card);
        const currentCards = groupedCards.get(groupKey) ?? [];

        currentCards.push(card);
        groupedCards.set(groupKey, currentCards);
    });

    return Array.from(groupedCards.values()).map((systemCards) => {
        const firstCard = systemCards[0];
        const system = firstCard.system || firstCard.sensor;
        const address = systemCards.find((card) => card.address)?.address ?? null;

        return {
            ...createAggregatedCardData(systemCards, system),
            address,
            name: firstCard.name || system,
            system,
        };
    });
}

export function aggregateOverviewCardsByAnomaly(
    cards: OverviewSystemAggregationSource[],
): OverviewAnomalyCardData[] {
    const groupedCards = new Map<string, OverviewSystemAggregationSource[]>();

    cards.forEach((card) => {
        const currentCards = groupedCards.get(card.anomalyConfig.key) ?? [];

        currentCards.push(card);
        groupedCards.set(card.anomalyConfig.key, currentCards);
    });

    return Array.from(groupedCards.values()).map((anomalyCards) => {
        const anomalyConfig = anomalyCards[0].anomalyConfig;

        return {
            ...createAggregatedCardData(anomalyCards, anomalyConfig.label),
            anomalyConfig,
        };
    });
}

export function aggregateOverviewCardsByStatus(
    cards: OverviewSystemAggregationSource[],
): OverviewStatusCardData[] {
    const groupedCards = new Map<OverviewStatusCardData['statusCategory'], OverviewSystemAggregationSource[]>();

    cards.forEach((card) => {
        const statusCategory = getOverviewStatusCategory(card.status);
        const currentCards = groupedCards.get(statusCategory) ?? [];

        currentCards.push(card);
        groupedCards.set(statusCategory, currentCards);
    });

    return Array.from(groupedCards.entries()).map(([statusCategory, statusCards]) => ({
        ...createAggregatedCardData(statusCards, statusCategory),
        statusCategory,
    }));
}

export function aggregateOverviewCardsByCustomer(
    cards: OverviewSystemAggregationSource[],
): OverviewCustomerCardData[] {
    const groupedCards = new Map<string, OverviewSystemAggregationSource[]>();

    cards.forEach((card) => {
        const customer = card.customer || card.sensor;
        const currentCards = groupedCards.get(customer) ?? [];

        currentCards.push(card);
        groupedCards.set(customer, currentCards);
    });

    return Array.from(groupedCards.entries()).map(([customer, customerCards]) => ({
        ...createAggregatedCardData(customerCards, customer),
        customer,
    }));
}

export function sortOverviewAggregatedCardsByKpi<T extends {
    kpis: OverviewSystemKpiData[];
    sensorCount: number;
    status: number;
    statusCounts: OverviewStatusCounts;
}>(
    cards: T[],
    sensorType: string,
    direction: OverviewSortDirection,
): T[] {
    const directionFactor = direction === 'desc' ? -1 : 1;
    const statusCountKey = STATUS_SENSOR_COUNT_SORT_KEYS.find((key) => key === sensorType);
    const statusCount = statusCountKey
        ? STATUS_SENSOR_COUNT_SORT_KEY_TO_STATUS[statusCountKey]
        : null;

    return [...cards].sort((leftCard, rightCard) => {
        const leftValue = sensorType === 'status'
            ? leftCard.status
            : sensorType === SYSTEM_SENSOR_COUNT_SORT_KEY
                ? leftCard.sensorCount
                : statusCount
                    ? leftCard.statusCounts[statusCount]
                : leftCard.kpis.find((kpi) => kpi.sensorType === sensorType)?.value ?? null;
        const rightValue = sensorType === 'status'
            ? rightCard.status
            : sensorType === SYSTEM_SENSOR_COUNT_SORT_KEY
                ? rightCard.sensorCount
                : statusCount
                    ? rightCard.statusCounts[statusCount]
                : rightCard.kpis.find((kpi) => kpi.sensorType === sensorType)?.value ?? null;

        if (leftValue === null && rightValue === null) {
            return 0;
        }

        if (leftValue === null) {
            return 1;
        }

        if (rightValue === null) {
            return -1;
        }

        return (leftValue - rightValue) * directionFactor;
    });
}

export function sortOverviewSystemCardsByKpi(
    cards: OverviewSystemCardData[],
    sensorType: string,
    direction: OverviewSortDirection,
): OverviewSystemCardData[] {
    return sortOverviewAggregatedCardsByKpi(cards, sensorType, direction);
}
